import React, { Component } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import { injectIntl } from 'react-intl'
import { Grid } from '@material-ui/core'
import { isEmpty, isEqual } from 'lodash'
import SelectList from 'components/common/SelectList'
import { setSiteContext } from 'reducers/profile/reducer'
import DateRangeInput from 'components/common/Form/DateRange'
import {
  fetchSiteList,
  fetchSensorTypes,
  fetchSensorNames,
  fetchSiteTypes,
  setActiveTypes,
  setActiveNames,
  setActiveDateRange,
  resetActiveSensorTypes,
  resetActiveSensorNames,
  resetSensorNameList,
  resetSensorsData,
} from 'reducers/sensor/reducer'
import MultiSelect from 'components/common/Form/MultiSelect'
import ArrayHelper from 'helpers/array'
import withPromise from 'helpers/withPromise'
import { getDateFormat } from 'helpers/dates'
import SensorMultiSelect from './SensorMultiSelect'
import messages from './translate'

export const SENSOR_NAMES_LIMIT = 20

class FilterBar extends Component {
  state = {
    activeSiteType: null,
  }

  componentDidMount() {
    const { activeSite, range, fetchSiteTypes, fetchSensorTypes } = this.props

    fetchSiteTypes({
      has_sites_with_active_sensors: 1,
    })

    fetchSensorTypes({
      site: activeSite,
      from: this.getDate(range, 'startDate'),
      to: this.getDate(range, 'endDate'),
    })
  }

  shouldComponentUpdate(nextProps, nextState) {
    const {
      siteTypes,
      fetchSiteList,
      resetActiveSensorNames,
      resetSensorNameList,
    } = this.props

    if (siteTypes !== nextProps.siteTypes) {
      const firstType = nextProps.siteTypes.get(0)

      if (firstType) {
        this.setState({
          activeSiteType: firstType.id,
        })
      }

      return false
    }

    if (this.state.activeSiteType !== nextState.activeSiteType) {
      resetActiveSensorNames()
      resetSensorNameList()
      fetchSiteList({
        params: {
          site_type: nextState.activeSiteType,
          has_active_sensors: 1,
        },
      })

      return false
    }

    return true
  }

  componentDidUpdate(prevProps) {
    const {
      activeSite,
      activeNames,
      siteList,
      range,
      setSiteContext,
      activeTypes,
      fetchSensorTypes,
      resetActiveSensorTypes,
      resetActiveSensorNames,
      resetSensorNameList,
    } = this.props

    if (siteList !== prevProps.siteList && siteList.size > 0) {
      if (
        activeSite === 0 ||
        (activeSite !== 0 &&
          !ArrayHelper.hasInArray(siteList.toArray(), activeSite, 'id'))
      ) {
        setSiteContext({ site: siteList.get(0).id })
      }
    }

    if (
      this.getDate(range, 'startDate') !==
        this.getDate(prevProps.range, 'startDate') ||
      this.getDate(range, 'endDate') !==
        this.getDate(prevProps.range, 'endDate') ||
      activeSite !== prevProps.activeSite
    ) {
      if (activeTypes.size > 0) {
        resetActiveSensorTypes()
      }

      if (activeNames.size > 0) {
        resetActiveSensorNames()
        resetSensorNameList()
      }

      fetchSensorTypes({
        site: activeSite,
        from: this.getDate(range, 'startDate'),
        to: this.getDate(range, 'endDate'),
      })
    }
  }

  getDate = (date, type) => date.get(type).format('YYYY-MM-DD')

  onSiteFilterChange = siteValue => {
    if (siteValue) {
      const { siteList, activeSite } = this.props

      if (activeSite !== siteValue.index) {
        this.props.setSiteContext({ site: siteList.get(siteValue.mapKey).id })
      }
    }
  }

  onSiteTypeChange = value => {
    if (value && this.state.activeSiteType !== value.index) {
      this.setState({
        activeSiteType: value.index,
      })
    }
  }

  fetchSensorNames = types => {
    if (!Array.isArray(types) && !types.length) return
    const { activeSite, range, fetchSensorNames } = this.props
    const sensorTypeIds = types.map(sensor => sensor.id)

    withPromise(fetchSensorNames, {
      site: activeSite,
      from: this.getDate(range, 'startDate'),
      to: this.getDate(range, 'endDate'),
      sensor_types: sensorTypeIds,
    }).then(() => {
      this.setSensorNamesValue()
    })
  }

  onTypeFilterChange = types => {
    if (!isEmpty(types)) {
      this.props.setActiveTypes(types)
      this.fetchSensorNames(types)
    }
  }

  onNameFilterChange = names => {
    const { activeNames } = this.props
    const activeSensorNames = activeNames.toArray()

    if (!isEqual(activeSensorNames, names)) {
      this.props.setActiveNames(names)
    }
  }

  onDateRangeChange = range => {
    if (range) {
      let { startDate, endDate } = range

      if (!moment.isMoment(startDate)) {
        startDate = moment(startDate)
      }

      if (!moment.isMoment(endDate)) {
        endDate = moment(endDate)
      }

      this.props.setActiveDateRange({
        startDate,
        endDate,
      })
    }
  }

  doesActiveSiteExist = () => {
    const { activeSite, siteList } = this.props
    const sites = siteList.toArray()

    return Boolean(activeSite && !!sites.find(item => item.id === activeSite))
  }

  limitSensorNameList = (sensorNameList, limit) =>
    sensorNameList.length > limit
      ? sensorNameList.slice(0, limit)
      : sensorNameList

  setSensorNamesValue = () => {
    const { nameList } = this.props
    const sensorNameList = nameList.toArray()
    const sensorNameListLimited = this.limitSensorNameList(
      sensorNameList,
      SENSOR_NAMES_LIMIT,
    )

    if (!isEmpty(sensorNameListLimited)) {
      this.props.setActiveNames(sensorNameListLimited)
    }
  }

  render() {
    const {
      siteList,
      siteTypes,
      typeList,
      nameList,
      activeSite,
      activeTypes,
      activeNames,
      typesLoader,
      namesLoader,
      chartLoader,
      sitesLoader,
      siteTypesLoader,
      intl: { formatMessage },
      licensee: { usaDateFormat },
    } = this.props
    const selectedTypes = activeTypes.toArray()
    const selectedSite = this.doesActiveSiteExist() ? activeSite : null
    const disableSiteTypes = siteTypes.size === 0
    const disableSiteList = siteList.size === 0
    const disableSensorTypes = typeList.toArray().length === 0
    const selectedNames = activeNames.toArray()
    const sensorNameList = nameList.toArray()
    const disableSensorNames = !sensorNameList.length

    const dropdownMenuProps = {
      classes: {
        list: 'sensor-names-list',
      },
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
      getContentAnchorEl: null,
    }

    return (
      <Grid container spacing={1} className="module-filter">
        <Grid item lg={2} md={4} sm={4} xs={12}>
          <SelectList
            name="sensor-site"
            label={formatMessage(messages.siteType)}
            onChange={this.onSiteTypeChange}
            list={siteTypes}
            value={this.state.activeSiteType}
            loader={siteTypesLoader}
            disabled={disableSiteTypes}
            map={{
              index: 'id',
              text: 'name',
            }}
          />
        </Grid>
        <Grid item lg={2} md={4} sm={4} xs={12}>
          <SelectList
            name="sensor-site"
            label={formatMessage(messages.sensorSite)}
            onChange={this.onSiteFilterChange}
            list={siteList}
            value={selectedSite}
            loader={sitesLoader}
            disabled={disableSiteList}
            map={{
              index: 'id',
              text: 'name',
            }}
          />
        </Grid>
        <Grid item lg={2} md={4} sm={4} xs={12}>
          <DateRangeInput
            label={formatMessage(messages.dateRange)}
            onChange={this.onDateRangeChange}
            dateDisplayFormat={getDateFormat(usaDateFormat)}
          />
        </Grid>
        <Grid item lg={2} md={4} sm={4} xs={12}>
          <MultiSelect
            label={formatMessage(messages.sensorType)}
            name="sensor-type"
            data={typeList.toArray()}
            value={selectedTypes}
            loader={typesLoader}
            onChange={this.onTypeFilterChange}
            disabled={disableSensorTypes}
            disabledItem={chartLoader}
          />
        </Grid>
        <Grid item lg={2} md={4} sm={4} xs={12}>
          <SensorMultiSelect
            name="sensorName"
            label={formatMessage(messages.sensorName)}
            data={sensorNameList}
            value={selectedNames}
            disabled={disableSensorNames}
            disabledItem={chartLoader}
            loader={namesLoader}
            selectedItemsLimit={SENSOR_NAMES_LIMIT}
            onChange={this.onNameFilterChange}
            onClose={this.onNameFilterChange}
            MenuProps={dropdownMenuProps}
          />
        </Grid>
      </Grid>
    )
  }
}

export default connect(
  state => ({
    siteList: state.getIn(['sensor', 'siteList']),
    siteTypes: state.getIn(['sensor', 'siteTypes']),
    typeList: state.getIn(['sensor', 'sensorTypes']),
    nameList: state.getIn(['sensor', 'sensorNames']),
    activeSite: state.getIn(['profile', 'siteId']),
    range: state.getIn(['sensor', 'dateRange']),
    activeTypes: state.getIn(['sensor', 'activeTypes']),
    typesLoader: state.getIn(['sensor', 'sensorsTypesLoader']),
    activeNames: state.getIn(['sensor', 'activeNames']),
    namesLoader: state.getIn(['sensor', 'sensorsNamesLoader']),
    chartLoader: state.getIn(['sensor', 'sensorsChartLoader']),
    sitesLoader: state.getIn(['sensor', 'siteListLoader']),
    siteTypesLoader: state.getIn(['sensor', 'siteTypesLoader']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
  }),
  {
    fetchSiteList,
    fetchSiteTypes,
    setSiteContext,
    fetchSensorTypes,
    fetchSensorNames,
    setActiveTypes,
    setActiveNames,
    setActiveDateRange,
    resetActiveSensorTypes,
    resetActiveSensorNames,
    resetSensorNameList,
    resetSensorsData,
  },
)(injectIntl(FilterBar))
